import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import {
    doc,
    updateDoc,
    getDocs,
    collection,
    arrayRemove,
    arrayUnion,
    writeBatch
} from "firebase/firestore";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import Divider from "../../components/Divider/Divider";

export default function AccountSettings() {
    const { user, updateUserProfile, updatePassword } = useAuth();

    const [formData, setFormData] = useState({
        firstName: user?.firstName || user?.displayName?.split(" ")[0] || "",
        lastName: user?.lastName || user?.displayName?.split(" ")[1] || "",
        email: user?.email || "",
    });

    const [passwordData, setPasswordData] = useState({
        current: "",
        new: "",
        confirm: "",
    });

    const [message, setMessage] = useState("");
    const [passwordMessage, setPasswordMessage] = useState("");
    const [loading, setLoading] = useState(false);
    // const [imageError, setImageError] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setMessage("");
        setLoading(true);

        try {
            const { firstName, lastName, email } = formData;
            const newDisplayName = `${firstName} ${lastName}`;

            // 1. Update displayName in Firebase Auth
            await updateUserProfile({ displayName: newDisplayName });

            // 2. Update Firestore user document
            await updateDoc(doc(db, "users", user.uid), {
                firstName,
                lastName,
                email,
            });

            // 3. Update all lists where user is a member or owner
            const batch = writeBatch(db);
            const listsSnapshot = await getDocs(collection(db, "lists"));
            for (const listDoc of listsSnapshot.docs) {
                const listData = listDoc.data();
                if ((listData.members || []).includes(user.uid)) {
                    const listRef = doc(db, "lists", listDoc.id);

                    // Remove old memberInfo entry
                    const oldInfo = listData.memberInfo?.find((m) => m.uid === user.uid);
                    if (oldInfo) {
                        batch.update(listRef, {
                            memberInfo: arrayRemove(oldInfo),
                        });
                    }

                    // Add updated memberInfo entry
                    batch.update(listRef, {
                        memberInfo: arrayUnion({
                            uid: user.uid,
                            displayName: newDisplayName,
                        }),
                    });
                }
            }

            // Commit all updates
            await batch.commit();

            setMessage("Profile updated successfully!");
        } catch (err) {
            console.error(err);
            setMessage("Failed to update profile.");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setPasswordMessage("");
        setLoading(true);

        if (passwordData.new !== passwordData.confirm) {
            setPasswordMessage("New passwords do not match.");
            setLoading(false);
            return;
        }

        try {
            await updatePassword(passwordData.current, passwordData.new);
            setPasswordMessage("Password updated successfully!");
            setPasswordData({ current: "", new: "", confirm: "" });
        } catch (err) {
            console.error(err);
            setPasswordMessage(err.message || "Failed to update password.");
        } finally {
            setLoading(false);
        }
    };

    // const avatarLetter =
    //     user.displayName?.[0]?.toUpperCase() || user.email[0]?.toUpperCase();
    const isGoogleUser = user.providerData?.some((p) =>
        p.providerId.includes("google")
    );

    return (
        <div className="max-w-3xl mx-auto mt-10 min-h-full px-6 md:px-0">
            <div className="max-w-xl mx-auto mt-12 bg-white shadow-md rounded-lg p-6 space-y-8">
                <h2 className="text-xl font-semibold">
                    Account Settings
                </h2>
                <Divider />

                {/* Avatar */}
                {/* <div className="w-20 h-20 rounded-full overflow-hidden bg-surface flex items-center justify-center text-3xl mb-6">
                    {!imageError && user.photoURL ? (
                        <img
                            src={user.photoURL}
                            alt="avatar"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <span>{avatarLetter}</span>
                    )}
                </div> */}

                <form onSubmit={handleSave} className="space-y-6">
                    {/* Name & Email */}
                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Input
                                label="First Name"
                                className="flex-1"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder="First Name"
                                required
                            />

                            <Input
                                label="Last Name"
                                className="flex-1"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder="Last Name"
                                required
                            />
                        </div>

                        <Input
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="name@email.com"
                            required
                            disabled={isGoogleUser}
                        />
                        {isGoogleUser && (
                            <p className="text-sm">
                                Email is managed via your Google account and cannot
                                be changed here.
                            </p>
                        )}
                    </div>

                    {/* Password Section */}
                    <div className="space-y-3">
                        <h3 className="text-xl font-semibold mb-2">
                            Reset Password
                        </h3>
                        <Input
                            label="Current Password"
                            type="password"
                            name="current"
                            value={passwordData.current}
                            onChange={handlePasswordChange}
                            placeholder="Current Password"
                            disabled={isGoogleUser}
                        />

                        <Input
                            label="New Password"
                            type="password"
                            name="new"
                            value={passwordData.new}
                            onChange={handlePasswordChange}
                            placeholder="New Password"
                            disabled={isGoogleUser}
                            required={!isGoogleUser}
                        />

                        <Input
                            label="Confirm New Password"
                            type="password"
                            name="confirm"
                            value={passwordData.confirm}
                            onChange={handlePasswordChange}
                            placeholder="Confirm Password"
                            disabled={isGoogleUser}
                            required={!isGoogleUser}
                        />

                        {isGoogleUser ? (
                            <p className="text-sm">
                                Password is managed via your Google account and
                                cannot be changed here.
                            </p>
                        ) : (
                            <div className="flex">
                                <Button
                                    type="button"
                                    onClick={handlePasswordReset}
                                    variant="secondary"
                                    disabled={loading}
                                    className="mt-4 w-full"
                                >
                                    {loading ? "Updating..." : "Update Password"}
                                </Button>
                            </div>
                        )}

                        {passwordMessage && (
                            <p className="mt-2 text-sm text-center">
                                {passwordMessage}
                            </p>
                        )}
                    </div>

                    <Divider />
                    <div className="flex">
                        <Button type="submit" variant="primary" disabled={loading} className="w-full">
                            {loading ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </form>

                {message && (
                    <p className="mt-2 text-sm text-center">
                        {message}
                    </p>
                )}
            </div>
        </div>
    );
}
